import { useAbility } from '@casl/react';
import {
  Box,
  CircularProgress,
  Grid,
  Typography,
  lighten,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ACTIONS } from 'src/auth/utils/defineAbilityFor';
import EntityIcon from 'src/components/Icons/EntityIcon';
import { AbilityContext } from 'src/context/canContext';
import { useAdminUrlNavigation } from 'src/hooks/useAdminUrlNavigation';
import { useApplications } from 'src/hooks/useApplications';
import useEffectiveApplicationGroupId from 'src/hooks/useEffectiveApplicationGroupId';
import useEffectiveApplicationId from 'src/hooks/useEffectiveApplicationId';
import { hasConcreteSelection, isAllSelection } from 'src/utils/selection';
import WidgetCard from 'switch-fe-shared/components/Cards/WidgetCard';

import { formatNumber } from '../../../../../../switch-shared/src/utils/formatters';
import { useDashboard } from '../../../../context/dashboardContext';
import pages from '../../../../router/routes';
import { useGetAllAccountsQuery } from '../../../../services/switch-rest-api/accounts.service';
import { useGetZoneAccountsQuery } from '../../../../services/switch-rest-api/meter-point.service';
import { useAllGroupsMetrics } from '../../hooks/useAllGroupsMetrics';

const CountWidget = () => {
  /* Hooks & Use Effects */

  const theme = useTheme();
  const ability = useAbility(AbilityContext);

  const navigate = useNavigate();
  const { getApplicationScopedPath, navigateToApplications } =
    useAdminUrlNavigation();

  const appId = useEffectiveApplicationId();
  const { applications, isLoading: isApplicationsLoading } = useApplications();

  const appGroupId = useEffectiveApplicationGroupId();

  const { zone } = useDashboard();

  /* States */
  const isAllGroupsSelected = isAllSelection(appGroupId);

  /* All Groups Metrics Hook */
  const {
    metrics: allGroupsMetrics,
    isLoading: isAllGroupsLoading,
    isFetching: isAllGroupsFetching,
  } = useAllGroupsMetrics();

  /* API Calls */

  const {
    data: accounts,
    isLoading,
    isFetching,
  } = useGetAllAccountsQuery(
    {
      applicationId: appId,
      limit: '1',
    },
    {
      skip: appId == null || isAllSelection(appId) || zone != null,
      pollingInterval: 30_000,
    }
  );

  const {
    data: zoneAccounts,
    isLoading: isZoneAccountsLoading,
    isFetching: isZoneAccountsFetching,
  } = useGetZoneAccountsQuery(
    {
      zoneId: zone?._id ?? '',
      limit: 0,
    },
    {
      skip: zone == null || isAllGroupsSelected,
      pollingInterval: 30_000,
    }
  );

  /* Functions & Functional Components */

  const getTooltipDescription = () => {
    if (zone?._id) {
      return `The total number of accounts in zone ${zone.name}`;
    }
    if (hasConcreteSelection(appId)) {
      return 'The total number of accounts in the selected application';
    }
    if (isAllGroupsSelected) {
      return 'The total number of applications across all groups';
    }
    return 'The total number of applications in the selected group';
  };

  return (
    <WidgetCard
      title={hasConcreteSelection(appId) ? 'Accounts' : 'Applications'}
      subheader={zone?.name ? `Total count in ${zone.name}` : 'Total count'}
      tooltipDescription={getTooltipDescription()}
      disabled={
        !ability.can(
          ACTIONS.READ,
          hasConcreteSelection(appId) ? 'Accounts' : 'Applications'
        )
      }
      // avatar={<MoreVert sx={{ color: theme.tokens.colors.primary }} />}
      avatar={
        <Box
          sx={{
            padding: `${theme.tokens.spacing.sm}px`,
            height: `${theme.tokens.spacing.sm * 5}px`,
            width: `${theme.tokens.spacing.sm * 5}px`,
            mx: `-${theme.tokens.spacing.sm}px`,
            borderRadius: theme.tokens.borderRadius.full,
            background: theme.tokens.colors.card,
            boxShadow: theme.tokens.shadows.sm,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: theme.palette.primary.main,
          }}
        >
          <EntityIcon
            type={isAllSelection(appId) ? 'applications' : 'accounts'}
          />
        </Box>
      }
      onClickFn={
        !isAllGroupsSelected
          ? isAllSelection(appId)
            ? () => navigateToApplications(appGroupId)
            : () =>
                navigate(
                  getApplicationScopedPath('accounts', pages.accounts.path)
                )
          : undefined
      }
      cardContentProps={{
        sx: {
          pt: `0 !important`,
          pb: `16px !important`,
          color: lighten(theme.palette.primary.main, 0.1),
        },
      }}
    >
      <Grid
        container
        alignContent={'center'}
        justifyContent={'center'}
        height={'100%'}
      >
        <Grid item>
          {appId == null || isAllSelection(appId) ? (
            <>
              {isAllGroupsSelected ? (
                <>
                  {isAllGroupsLoading || isAllGroupsFetching ? (
                    <CircularProgress />
                  ) : (
                    <Typography
                      variant='h4'
                      fontSize={'25px'}
                      fontWeight={'bold'}
                    >
                      {formatNumber(
                        allGroupsMetrics?.totalApplications ?? 0,
                        0
                      )}
                    </Typography>
                  )}
                </>
              ) : isApplicationsLoading ? (
                <CircularProgress />
              ) : (
                <Typography variant='h4' fontSize={'25px'} fontWeight={'bold'}>
                  {formatNumber(
                    applications.filter((app) => !isAllSelection(app._id))
                      .length,
                    0
                  )}
                </Typography>
              )}
            </>
          ) : (
            <>
              {zone == null ? (
                <>
                  {isLoading || isFetching ? (
                    <CircularProgress />
                  ) : (
                    <Typography
                      variant='h4'
                      fontSize={'25px'}
                      fontWeight={'bold'}
                    >
                      {formatNumber(accounts?.totalRecords ?? 0, 0)}
                    </Typography>
                  )}
                </>
              ) : (
                <>
                  {isZoneAccountsLoading || isZoneAccountsFetching ? (
                    <CircularProgress />
                  ) : (
                    <Typography
                      variant='h4'
                      fontSize={'25px'}
                      fontWeight={'bold'}
                    >
                      {formatNumber(zoneAccounts?.totalRecords ?? 0, 0)}
                    </Typography>
                  )}
                </>
              )}
            </>
          )}
        </Grid>
      </Grid>
    </WidgetCard>
  );
};

export default CountWidget;
